import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, CalendarClock } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

function ArchivedBanner({ trip, onChanged }) {
  const api = useApi();
  const restore = async () => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/restore`);
      toast.success("Trip restored to the active list");
      onChanged(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="lux-card p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="trip-archived-banner">
      <Archive size={20} className="text-zinc-500 shrink-0" />
      <p className="flex-1 text-sm text-zinc-300">Archived {fmtDate(trip.archived_at)}{trip.archived_reason ? ` · ${trip.archived_reason}` : ""}. Only admins can see this trip.</p>
      <button className="frame-btn" onClick={restore} data-testid="trip-restore-btn">Restore</button>
    </div>
  );
}

function NoLongerActive({ onConfirm, busy }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="frame-btn" disabled={busy} data-testid="trip-inactive-btn">No longer active</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#111319] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-display text-2xl font-normal">Cancel this trip?</AlertDialogTitle>
          <AlertDialogDescription>The trip is cancelled, removed from your list and the flight department is told.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="trip-inactive-cancel-btn">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="trip-inactive-confirm-btn">Yes, cancel it</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function TripLifecycleBanner({ trip, isAdmin, isBroker, onChanged }) {
  const api = useApi();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (trip.archived_at) return isAdmin ? <ArchivedBanner trip={trip} onChanged={onChanged} /> : null;
  const open = ["new", "options_sent"].includes(trip.status);
  if (!isBroker || !open || !trip.active_reminder_sent_at || trip.active_confirmed_at) return null;
  const answer = async (active) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/active-check`, { active });
      if (active) {
        toast.success("Thanks. We'll keep this trip until the day of departure.");
        onChanged(data);
      } else {
        toast.success("Trip cancelled. The flight department has been notified.");
        navigate("/");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lux-card !border-[#C6A55C] p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="trip-active-check-banner">
      <CalendarClock size={22} className="text-[#C6A55C] shrink-0" strokeWidth={1.5} />
      <p className="flex-1 text-sm text-zinc-200">Leg 1 departs {fmtDate(trip.legs[0].date)}. Is this trip still active?</p>
      <div className="flex flex-wrap gap-2">
        <button className="frame-btn gold" disabled={busy} onClick={() => answer(true)} data-testid="trip-still-active-btn">Still active</button>
        <NoLongerActive busy={busy} onConfirm={() => answer(false)} />
      </div>
    </div>
  );
}
